import { useMemo, useState } from 'react';
import { useDeleteMtoLotMutation, useListMtoLotsQuery } from '@shared/api';
import type { MaterialLot } from '@entities/mto';
import { apiErrorMessage, fmtDate } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { Input, Label } from '@shared/ui/Input';
import { IconChevronDown, IconChevronRight, IconTrash } from '@shared/ui/icons';
import { AddLotForm } from '../components/AddLotForm';
import { LotDocs } from '../components/LotDocs';
import { lotLabel, qtyWithUnit, siteOrDash } from '../helpers';
import {
  DocsCell,
  Empty,
  ErrBox,
  ExpandButton,
  FilterBar,
  FilterField,
  IconButton,
  LotName,
  Muted,
  SectionTitle,
  Table,
  TableScroll,
  Wrap,
} from '../styles';

export function LotRow({
  lot,
  open,
  canWrite,
  onToggle,
  onDelete,
}: {
  lot: MaterialLot;
  open: boolean;
  canWrite: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <tr>
        <td>
          <ExpandButton type="button" $open={open} aria-expanded={open} onClick={onToggle}>
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <LotName>{lot.lot_no && lot.lot_no.trim() ? lot.lot_no : 'без №'}</LotName>
          </ExpandButton>
        </td>
        <td>{lot.nomenclature}</td>
        <td>{lot.supplier ?? '—'}</td>
        <td>{fmtDate(lot.supply_date)}</td>
        <td className="num">{qtyWithUnit(lot.quantity, lot.unit)}</td>
        <td>{siteOrDash(lot.site_id)}</td>
        <td>
          <IconButton
            type="button"
            onClick={onDelete}
            disabled={!canWrite}
            aria-label={`Удалить партию ${lotLabel(lot)}`}
            title={!canWrite ? 'Только просмотр' : 'Удалить партию'}
          >
            <IconTrash size={15} />
          </IconButton>
        </td>
      </tr>
      {open && (
        <tr>
          <DocsCell colSpan={7}>
            <LotDocs lotId={lot.id} canWrite={canWrite} />
          </DocsCell>
        </tr>
      )}
    </>
  );
}

export function LotsTab({ canWrite }: { canWrite: boolean }) {
  const [site, setSite] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MaterialLot | null>(null);

  const siteFilter = site.trim();
  const { data, isLoading } = useListMtoLotsQuery(siteFilter ? { site_id: siteFilter } : undefined);
  const [deleteLot, { isLoading: deleting }] = useDeleteMtoLotMutation();
  const lots = useMemo<MaterialLot[]>(() => data?.items ?? [], [data]);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setError(null);
    try {
      await deleteLot(id).unwrap();
      if (openId === id) setOpenId(null);
      setPendingDelete(null);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось удалить партию.');
      setPendingDelete(null);
      // eslint-disable-next-line no-console
      console.error('deleteMtoLot failed', err);
    }
  }

  return (
    <Wrap>
      <FilterBar>
        <FilterField>
          <Label htmlFor="qc-lots-site">Фильтр по объекту</Label>
          <Input
            id="qc-lots-site"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder="Объект, например ДГП-2 (пусто — все)"
          />
        </FilterField>
      </FilterBar>

      <Card>
        <SectionTitle>
          Реестр партий <Badge $tone="neutral">{lots.length}</Badge>
        </SectionTitle>
        {isLoading ? (
          <Muted>Загрузка партий…</Muted>
        ) : lots.length === 0 ? (
          <Empty>
            {siteFilter
              ? 'По выбранному объекту партий нет.'
              : 'Партий пока нет. Добавьте первую партию материала ниже.'}
          </Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">№ партии</th>
                  <th scope="col">Наименование</th>
                  <th scope="col">Поставщик</th>
                  <th scope="col">Дата поставки</th>
                  <th scope="col" className="num">
                    Кол-во
                  </th>
                  <th scope="col">Объект</th>
                  <th scope="col" aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {lots.map((lot) => (
                  <LotRow
                    key={lot.id}
                    lot={lot}
                    open={openId === lot.id}
                    canWrite={canWrite}
                    onToggle={() => setOpenId((cur) => (cur === lot.id ? null : lot.id))}
                    onDelete={() => setPendingDelete(lot)}
                  />
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        {error && <ErrBox>{error}</ErrBox>}
      </Card>

      {canWrite && <AddLotForm canWrite={canWrite} onError={(m) => setError(m || null)} />}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Удалить партию?"
        warning="Партия и все её документы качества и факты применения будут удалены безвозвратно. Это действие нельзя отменить."
        confirmText="удалить"
        confirmLabel="Введите слово «удалить» для подтверждения"
        confirmButtonLabel="Удалить партию"
        busyLabel="Удаление…"
        isBusy={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Wrap>
  );
}
